
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';
import TopNavigation from '../../../components/feature/TopNavigation';
import Button from '../../../components/base/Button';

export default function PaymentPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [rating, setRating] = useState(0);
  const [feedback, setFeedback] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('cash');
  
  const tripData = location.state || {
    pickup: 'الرياض، حي الملز',
    destination: 'الرياض، حي النخيل',
    price: 15,
    driver: {
      name: 'أحمد محمد',
      rating: 4.8
    }
  };

  const paymentMethods = [
    { id: 'cash', name: 'نقداً', icon: 'ri-money-dollar-circle-line' },
    { id: 'mada', name: 'مدى', icon: 'ri-bank-card-line' },
    { id: 'visa', name: 'فيزا', icon: 'ri-visa-line' },
    { id: 'mastercard', name: 'ماستركارد', icon: 'ri-mastercard-line' }
  ];

  const handlePayment = () => {
    // Simulate payment processing
    setTimeout(() => {
      navigate('/home', { 
        state: { 
          message: t('paymentSuccess'),
          type: 'success'
        }
      });
    }, 1000);
  };

  return (
    <div className="min-h-screen bg-[#FDFDFD] pb-20">
      <TopNavigation title={t('payment')} />
      
      <div className="pt-20 px-4">
        {/* Trip Completed */}
        <div className="bg-gradient-to-r from-green-500 to-green-600 rounded-2xl p-6 mb-6 text-white text-center">
          <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
            <i className="ri-check-line text-white text-2xl"></i>
          </div>
          <h2 className="text-xl font-bold mb-2">{t('tripCompleted')}</h2>
          <p className="text-white/90">شكراً لاستخدام MovePro</p>
        </div>

        {/* Trip Summary */}
        <div className="bg-white rounded-2xl p-6 mb-6 shadow-sm border border-gray-100">
          <h3 className="text-lg font-bold text-gray-800 mb-4">ملخص الرحلة</h3>
          
          <div className="space-y-3 mb-4">
            <div className="flex items-center">
              <div className="w-3 h-3 bg-[#0056A6] rounded-full mr-3"></div>
              <p className="text-sm text-gray-700 flex-1">{tripData.pickup}</p>
            </div>
            <div className="border-r border-gray-200 mr-1.5 h-4"></div>
            <div className="flex items-center">
              <div className="w-3 h-3 border-2 border-[#F9A826] rounded-full mr-3"></div>
              <p className="text-sm text-gray-700 flex-1">{tripData.destination}</p>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4 pt-4 border-t border-gray-100">
            <div>
              <p className="text-xs text-gray-500 mb-1">المسافة</p>
              <p className="text-sm font-medium text-gray-800">12.5 كم</p>
            </div>
            <div>
              <p className="text-xs text-gray-500 mb-1">المدة</p>
              <p className="text-sm font-medium text-gray-800">18 دقيقة</p>
            </div>
            <div>
              <p className="text-xs text-gray-500 mb-1">السائق</p>
              <p className="text-sm font-medium text-gray-800">{tripData.driver.name}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500 mb-1">التاريخ</p>
              <p className="text-sm font-medium text-gray-800">{new Date().toLocaleDateString('ar-SA')}</p>
            </div>
          </div>
        </div>

        {/* Rating */}
        <div className="bg-white rounded-2xl p-6 mb-6 shadow-sm border border-gray-100">
          <h3 className="text-lg font-bold text-gray-800 mb-4">قيم تجربتك</h3>
          
          <div className="text-center mb-4">
            <div className="flex justify-center space-x-2 mb-4">
              {[1, 2, 3, 4, 5].map((star) => (
                <button
                  key={star}
                  onClick={() => setRating(star)}
                  className={`w-10 h-10 rounded-full flex items-center justify-center transition-colors duration-200 ${
                    star <= rating
                      ? 'bg-yellow-400 text-white'
                      : 'bg-gray-100 text-gray-400 hover:bg-gray-200'
                  }`}
                >
                  <i className="ri-star-fill text-lg"></i>
                </button>
              ))}
            </div>
            
            <textarea
              value={feedback}
              onChange={(e) => setFeedback(e.target.value)}
              placeholder="اكتب تعليقك (اختياري)"
              className="w-full h-20 p-3 border border-gray-200 rounded-xl resize-none focus:outline-none focus:border-[#0056A6] text-sm"
              maxLength={200}
            />
          </div>
        </div>

        {/* Payment Method */}
        <div className="bg-white rounded-2xl p-6 mb-6 shadow-sm border border-gray-100">
          <h3 className="text-lg font-bold text-gray-800 mb-4">طريقة الدفع</h3>
          
          <div className="space-y-3 mb-4">
            {paymentMethods.map((method) => (
              <div
                key={method.id}
                onClick={() => setPaymentMethod(method.id)}
                className={`flex items-center p-3 rounded-xl border-2 cursor-pointer transition-all duration-200 ${
                  paymentMethod === method.id
                    ? 'border-[#0056A6] bg-[#0056A6]/5'
                    : 'border-gray-100 hover:border-gray-200'
                }`}
              >
                <div className={`w-10 h-10 rounded-full flex items-center justify-center mr-3 ${
                  paymentMethod === method.id ? 'bg-[#0056A6] text-white' : 'bg-gray-100 text-gray-600'
                }`}>
                  <i className={`${method.icon} text-lg`}></i>
                </div>
                <span className="font-medium text-gray-800">{method.name}</span>
                {paymentMethod === method.id && (
                  <i className="ri-check-line text-[#0056A6] text-lg mr-auto"></i>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Price Breakdown */}
        <div className="bg-white rounded-2xl p-6 mb-6 shadow-sm border border-gray-100">
          <h3 className="text-lg font-bold text-gray-800 mb-4">تفاصيل السعر</h3>
          
          <div className="space-y-3">
            <div className="flex justify-between">
              <span className="text-gray-600">سعر الرحلة</span>
              <span className="font-medium">{tripData.price} ريال</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">رسوم الخدمة</span>
              <span className="font-medium">2 ريال</span>
            </div>
            <div className="flex justify-between text-green-600">
              <span>خصم العضوية</span>
              <span>-1 ريال</span>
            </div>
            <div className="border-t border-gray-100 pt-3 flex justify-between">
              <span className="text-lg font-bold text-gray-800">المجموع</span>
              <span className="text-xl font-bold text-[#0056A6]">{tripData.price + 1} ريال</span>
            </div>
          </div>
        </div>

        {/* Payment Button */}
        <Button
          onClick={handlePayment}
          className="w-full"
          size="lg"
        >
          <i className="ri-secure-payment-line mr-2"></i>
          {paymentMethod === 'cash' ? 'تأكيد الدفع نقداً' : t('payNow')}
        </Button>
      </div>
    </div>
  );
}
